import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  ConversionsReportService,
  ConversionReportItem,
  ConversionReportSummary,
  ConversionReportPagination,
  ConversionReportQueryParams,
} from '../../services/conversions-report.service';

@Component({
  selector: 'app-report-usdt-clp-general',
  templateUrl: './report-usdt-clp-general.component.html',
  styleUrls: ['./report-usdt-clp-general.component.sass'],
})
export class ReportUsdtClpGeneralComponent implements OnInit, OnDestroy {
  conversions: ConversionReportItem[] = [];
  summary: ConversionReportSummary | null = null;
  pagination: ConversionReportPagination = {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  };

  filterForm: FormGroup;
  loading = false;
  errorMessage = '';
  successMessage = '';
  expandedCardId: number | null = null;

  private destroy$ = new Subject<void>();

  // Opciones de límite por página
  pageSizeOptions = [10, 20, 50, 100];

  // Exponer Math para usar en el template
  Math = Math;

  // Columnas a mostrar (sin transactionId)
  displayedColumns = [
    'userId',
    'userName',
    'usdtAmount',
    'clpRate',
    'clpTotal',
    'conversionDate',
  ];

  constructor(
    private conversionsReportService: ConversionsReportService,
    private fb: FormBuilder,
  ) {
    this.filterForm = this.fb.group({
      userId: [''],
      startDate: [''],
      endDate: [''],
    });
  }

  toggleCard(id: number): void {
    this.expandedCardId = this.expandedCardId === id ? null : id;
  }

  ngOnInit(): void {
    // Suscribirse al estado de carga del servicio
    this.conversionsReportService.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((loading) => {
        this.loading = loading;
      });

    // Cargar datos iniciales
    this.loadConversionsReport();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Carga el reporte de conversiones con los filtros actuales
   */
  loadConversionsReport(): void {
    this.errorMessage = '';
    this.successMessage = '';

    const params: ConversionReportQueryParams = {
      page: this.pagination.page,
      limit: this.pagination.limit,
      ...this.getFiltersFromForm(),
    };

    this.conversionsReportService
      .getConversionsReport(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.conversions = response.conversions;
          this.summary = response.summary;
          this.pagination = response.pagination;
        },
        error: (error) => {
          this.errorMessage =
            error.message || 'Error al cargar el reporte de conversiones';
        },
      });
  }

  /**
   * Obtiene los filtros del formulario
   */
  private getFiltersFromForm(): Partial<ConversionReportQueryParams> {
    const filters: Partial<ConversionReportQueryParams> = {};
    const formValue = this.filterForm.value;

    if (formValue.userId && formValue.userId.trim()) {
      filters.userId = formValue.userId.trim();
    }
    if (formValue.startDate) {
      // Convertir Date a string en formato YYYY-MM-DD
      const date = new Date(formValue.startDate);
      filters.startDate = date.toISOString().split('T')[0];
    }
    if (formValue.endDate) {
      // Convertir Date a string en formato YYYY-MM-DD
      const date = new Date(formValue.endDate);
      filters.endDate = date.toISOString().split('T')[0];
    }

    return filters;
  }

  /**
   * Aplica los filtros y recarga el reporte
   */
  applyFilters(): void {
    this.pagination.page = 1; // Resetear a la primera página
    this.loadConversionsReport();
  }

  /**
   * Limpia los filtros
   */
  clearFilters(): void {
    this.filterForm.reset();
    this.pagination.page = 1;
    this.loadConversionsReport();
  }

  /**
   * Cambia la página actual
   */
  onPageChange(page: number): void {
    if (page < 1 || page > this.pagination.totalPages) {
      return;
    }
    this.pagination.page = page;
    this.loadConversionsReport();
  }

  /**
   * Cambia el tamaño de página
   */
  onPageSizeChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.pagination.limit = parseInt(target.value, 10);
    this.pagination.page = 1; // Resetear a la primera página
    this.loadConversionsReport();
  }

  /**
   * Maneja cambios en el paginador de Material
   */
  onPaginatorChange(event: any): void {
    this.pagination.page = event.pageIndex + 1; // Material usa index 0
    this.pagination.limit = event.pageSize;
    this.loadConversionsReport();
  }

  /**
   * Genera un array de números para la paginación
   */
  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    const currentPage = this.pagination.page;
    const totalPages = this.pagination.totalPages;

    if (totalPages <= maxPagesToShow) {
      // Mostrar todas las páginas si son pocas
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Mostrar páginas alrededor de la actual
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, currentPage + 2);

      if (currentPage <= 3) {
        endPage = maxPagesToShow;
      }
      if (currentPage >= totalPages - 2) {
        startPage = totalPages - maxPagesToShow + 1;
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }

    return pages;
  }

  /**
   * Exporta el reporte a Excel
   */
  exportToExcel(): void {
    this.errorMessage = '';
    this.successMessage = '';

    const params: ConversionReportQueryParams = this.getFiltersFromForm();

    this.conversionsReportService
      .exportToExcel(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          const filename = `conversiones-usdt-clp-${new Date().toISOString().split('T')[0]}.xlsx`;
          this.conversionsReportService.downloadExcelFile(blob, filename);
          this.successMessage = 'Archivo exportado exitosamente';
          setTimeout(() => {
            this.successMessage = '';
          }, 3000);
        },
        error: (error) => {
          this.errorMessage =
            error.message || 'Error al exportar el reporte a Excel';
        },
      });
  }

  /**
   * Formatea número a moneda USDT
   */
  formatUSDT(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 8,
    }).format(amount);
  }

  /**
   * Formatea número a moneda CLP
   */
  formatCLP(amount: number): string {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  /**
   * Formatea fecha a formato local
   */
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-CL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }
}
